import { Injectable, inject } from '@angular/core';
import { Observable, of, forkJoin, from, mergeMap, map, iif, EMPTY, toArray, catchError, take, switchMap, concatMap } from 'rxjs';
import { PartyService } from '../entity-services/party.service';
import { CoiService } from '../entity-services/coi.service';
import { BaaService } from '../entity-services/baa.service';
import { ContractService } from '../entity-services/contract.service';
import { PeopleService } from '../entity-services/people.service';
import { CriticalDatesService } from '../entity-services/critical-dates.service';
import { AssessmentsService } from '../entity-services/assessments.service';
import { EntityType } from '../../objects/interface-library';

@Injectable({
  providedIn: 'root'
})
export class ConnectionsService {
  private partyService = inject(PartyService);
  private coiService = inject(CoiService);
  private baaService = inject(BaaService);
  private contractService = inject(ContractService);
  private peopleService = inject(PeopleService);
  private criticalDateService = inject(CriticalDatesService);
  private assessmentsService = inject(AssessmentsService);

  // A map of entity types to their corresponding services.
  private entityServiceMap = {
    [EntityType.Party]: this.partyService,
    [EntityType.Coi]: this.coiService,
    [EntityType.Baa]: this.baaService,
    [EntityType.Contract]: this.contractService,
    [EntityType.Person]: this.peopleService,
    [EntityType.Date]: this.criticalDateService,
    [EntityType.Assessment]: this.assessmentsService
  };

  /**
   * Defines which properties on a Contract object correspond to which entity types.
   * This is a critical piece for the reverse lookup.
   */
  private readonly _contractPropertyMap = {
    [EntityType.Party]: ['coveredEntity', 'businessAssociateName'],
    [EntityType.Coi]: ['relatedCois'],
    [EntityType.Baa]: ['relatedBaas'],
    [EntityType.Person]: ['personnel']
  };

  /**
   * Retrieves a list of entities (BAAs, Contracts, or COIs) associated with a party.
   * This is a reusable, reactive function that replaces your old logic.
   *
   * @param partyId The unique ID of the party.
   * @param entityType The type of entity to fetch ('coi', 'baa', or 'contract').
   * @returns An observable of an array of the fully-populated entities.
   */
  public getAssociatedEntities(partyId: string, entityType: string): Observable<any[]> {
    // Start the reactive stream by getting the party object from the PartyService cache.
    return this.partyService.getPartyObject(partyId).pipe(
      // We use switchMap to flatten the inner observable from the service.
      switchMap(party => {
        const partyData = party as any;

        if (!partyData || !partyData[entityType] || partyData[entityType].length === 0) {
          // If no party or no entities, return an empty stream to avoid errors.
          return EMPTY;
        }

        const entityIds: string[] = partyData[entityType];

        // Now we turn the array of IDs into a stream of individual IDs.
        return from(entityIds).pipe(
          // For each ID in the stream, we make an HTTP request to get the full details.
          // mergeMap allows us to run these HTTP calls in parallel.
          mergeMap(id => iif(() => !!id, this.getDetailsById(id, entityType), of(null))),
          // toArray collects all the results into a single array when the stream completes.
          toArray(),
          // Catch any errors that might have occurred during the process.
          catchError(error => {
            console.error(`Error fetching associated entities for ${entityType}:`, error);
            return EMPTY;
          })
        );
      }),
      catchError(error => {
        console.error('Error fetching party details:', error);
        return EMPTY;
      })
    );
  }

  /**
   * Finds all unique contracts associated with a given list of hydrated entities.
   * This is a reactive "reverse lookup" function.
   *
   * @param entities An array of fully hydrated entities (e.g., Party, Person, COI).
   * @param currentContractId The ID of the contract to exclude from the results.
   * @returns An observable of a unique list of related contracts.
   */
  public getRelatedContracts(entities: any[], currentContractId: string): Observable<any[]> {
    if (!entities || entities.length === 0) {
      return of([]);
    }

    // The core of the logic starts with the stream of all contracts.
    return this.contractService.allContracts$.pipe(
      map(allContracts => {
        const relatedContracts = new Set<any>();

        // We iterate through all contracts to find matches.
        for (const contract of allContracts) {
          if (contract.contractId === currentContractId) {
            continue; // Skip the current contract
          }

          // We iterate through the input entities to check for associations.
          for (const entity of entities) {
            const entityType = entity?.entityType as EntityType; // Assuming entities have a type property
            const contractProperties = this._contractPropertyMap[entityType];

            if (!contractProperties || !entity) {
              continue;
            }

            // Check if the contract is associated with the current entity.
            const isAssociated = contractProperties.some(prop => {
              const contractPropValue = contract[prop];
              const entityId = entity.id; // Assuming all entities have an 'id' property

              if (Array.isArray(contractPropValue)) {
                // Check if the entity's ID is in the contract's array of IDs.
                return contractPropValue.includes(entityId);
              } else {
                // Check if the contract's ID property matches the entity's ID.
                return contractPropValue === entityId;
              }
            });

            if (isAssociated) {
              relatedContracts.add(contract);
            }
          }
        }
        return Array.from(relatedContracts);
      }),
      // Ensure we only take the latest value from the cache.
      take(1),
      catchError(error => {
        console.error('Error getting related contracts:', error);
        return of([]);
      })
    );
  }

  /**
   * Private helper function to get the details of a single entity.
   */
  private getDetailsById(id: string, entityType: string): Observable<any> {
    const service = this.entityServiceMap[entityType] as any;
    if (!service) {
      console.error(`No service found for entity type: ${entityType}`);
      return EMPTY;
    }
    // Assumes all services have a getPartyObject method that can take any ID
    return service.getPartyObject(id).pipe(
      catchError(error => {
        console.error(`Error fetching details for entity ID ${id} (${entityType}):`, error);
        return EMPTY;
      })
    );
  }

  private readonly _associationMap = {
    // [EntityType.Coi]: {
    //   [EntityType.Party]: (fromId: string, toId: string) => this.coiService.associateCoiWithParty(fromId, toId),
    //   [EntityType.Contract]: (fromId: string, toId: string) => this.coiService.associateCoiWithContract(fromId, toId)
    // },
    // [EntityType.Party]: {
    //   [EntityType.Coi]: (fromId: string, toId: string) => this.partyService.associatePartyWithCoi(fromId, toId)
    // }
    // Add other relationships as needed
  };

  /**
   * Associates one entity with another by calling the appropriate service function.
   *
   * @param fromEntityType The type of the entity initiating the association.
   * @param fromId The ID of the entity initiating the association.
   * @param toEntityType The type of the entity being associated.
   * @param toId The ID of the entity being associated.
   * @returns An observable that emits the result of the association.
   */
  public associateEntity(
    fromEntityType: EntityType,
    fromId: string,
    toEntityType: EntityType,
    toId: string
  ): Observable<any> {
    // Use a two-level lookup to find the correct handler function.
    const handler = this._associationMap[fromEntityType]?.[toEntityType];

    if (handler) {
      // If a handler is found, execute the function and return its observable.
      return handler(fromId, toId).pipe(
        catchError(error => {
          console.error(`An error occurred during entity association:`, error);
          return EMPTY;
        })
      );
    } else {
      // If no handler is found, log an error and return an empty observable.
      console.error(`No association defined for ${fromEntityType} to ${toEntityType}`);
      return EMPTY;
    }
  }


  /**
   * Associates a single 'from' entity with a list of 'to' entities.
   *
   * @param fromEntity The entity to be associated (e.g., a COI, a Contract).
   * @param fromEntityType The type of the 'from' entity (e.g., EntityType.Coi).
   * @param entitiesToAssociate An array of objects to associate with the 'from' entity.
   * @returns An observable of an array of results from each association call.
   */
  public associateWithAllRelatedEntities(
    fromEntity: any,
    fromEntityType: EntityType,
    entitiesToAssociate: { id: string, type: EntityType }[]
  ): Observable<any[]> {
    // Use `from` to turn the array of entities into a stream.
    return from(entitiesToAssociate).pipe(
      concatMap(entityToAssociate => {
        // Use the existing associateEntity function for each item.
        return this.associateEntity(
          fromEntityType,         // Now a generic parameter
          fromEntity.id,          // Assumes the entity has an 'id' property
          entityToAssociate.type,
          entityToAssociate.id
        ).pipe(
          // Handle individual errors so a single failure doesn't stop the whole process.
          catchError(error => {
            console.error(`Failed to associate with entity ${entityToAssociate.id}:`, error);
            return of(null); // Return a value to keep the stream alive.
          })
        );
      }),
      toArray() // Collect all results into a single array.
    );
  }





}
